import React from "react";
import { Link } from "react-router-dom";
import { TOPICS, BRANCHES } from "../data/content";
import {
  ArrowRight, Money, House, GraduationCap, ChartLineUp, Heartbeat,
  Briefcase, Scales, RocketLaunch, ShieldCheck, Compass, Users, Bank,
} from "@phosphor-icons/react";

const ICON_MAP = {
  Money, House, GraduationCap, ChartLineUp, Heartbeat,
  Briefcase, Scales, RocketLaunch, Bank,
};

export default function Home({ onOpenAuth }) {
  return (
    <main>
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1625199254769-4b4ae3e35cb6"
            alt=""
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-br from-[#0A1128]/95 via-[#0A1128]/85 to-[#0033A0]/80" />
        </div>

        <div className="relative max-w-7xl mx-auto px-6 md:px-10 pt-20 pb-24 md:pt-32 md:pb-40">
          <div className="max-w-3xl fade-up">
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-[#FCA5A5] mb-5">
              Service. Benefits. Future.
            </p>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-7xl font-black text-white leading-[1.05] tracking-tighter">
              Military benefits, <span className="text-[#FCA5A5]">finally</span> in plain English.
            </h1>
            <p className="mt-6 text-lg md:text-xl text-slate-200 leading-relaxed max-w-2xl">
              Pay, housing, the GI Bill, TRICARE, TSP, jobs by branch, and what to do after service — short explanations built for recruits, service members, and their families.
            </p>

            <div className="mt-10 flex flex-wrap items-center gap-3" data-testid="hero-cta-group">
              <Link
                to="/start-here"
                data-testid="hero-start-here-btn"
                className="inline-flex items-center gap-2 bg-[#D90429] hover:bg-[#B00020] text-white font-semibold py-3.5 px-7 rounded-md text-base transition-colors"
              >
                Start Here Guide <ArrowRight size={18} weight="bold" />
              </Link>
              <Link
                to="/quiz"
                data-testid="hero-quiz-btn"
                className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 backdrop-blur border border-white/20 text-white font-semibold py-3.5 px-7 rounded-md text-base transition-colors"
              >
                Take the Job Quiz
              </Link>
              <Link
                to="/compare"
                data-testid="hero-compare-btn"
                className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 backdrop-blur border border-white/20 text-white font-semibold py-3.5 px-7 rounded-md text-base transition-colors"
              >
                Compare the Branches
              </Link>
              <button
                onClick={() => onOpenAuth?.("register")}
                data-testid="hero-signup-btn"
                className="inline-flex items-center gap-2 text-white/90 hover:text-white font-semibold py-3.5 px-3 text-base"
              >
                Or sign up for updates →
              </button>
            </div>

            <div className="mt-10 flex flex-wrap gap-x-8 gap-y-3 text-sm text-slate-300">
              <div className="flex items-center gap-2"><ShieldCheck size={18} weight="bold" /> Non-partisan, no sales pitch</div>
              <div className="flex items-center gap-2"><Compass size={18} weight="bold" /> Built for beginners</div>
              <div className="flex items-center gap-2"><Users size={18} weight="bold" /> For families too</div>
            </div>
          </div>
        </div>
      </section>

      {/* TOPIC GRID */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 py-20 md:py-28">
        <div className="flex items-end justify-between gap-6 flex-wrap mb-12">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-[#0033A0]">Browse topics</p>
            <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tight text-slate-900 mt-2">
              Everything covered — one click away
            </h2>
          </div>
          <Link to="/topics" className="text-[#0033A0] font-semibold inline-flex items-center gap-1 hover:underline" data-testid="all-topics-link">
            All topics <ArrowRight size={16} weight="bold" />
          </Link>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {TOPICS.map((t, i) => {
            const Icon = ICON_MAP[t.icon] || Briefcase;
            const to = t.slug === "compare" ? "/compare" : t.slug === "jobs" ? "/branches" : `/topics/${t.slug}`;
            return (
              <Link
                key={t.slug}
                to={to}
                data-testid={`topic-card-${t.slug}`}
                className="group bg-white border border-slate-200 rounded-xl p-6 hover:border-[#0033A0] hover:shadow-lg hover:-translate-y-0.5 transition-all fade-up"
                style={{ animationDelay: `${i * 60}ms` }}
              >
                <div className="h-11 w-11 rounded-md bg-[#0033A0]/10 text-[#0033A0] flex items-center justify-center mb-4 group-hover:bg-[#0033A0] group-hover:text-white transition-colors">
                  <Icon size={22} weight="duotone" />
                </div>
                <h3 className="font-display font-bold text-slate-900 text-lg leading-snug">{t.title}</h3>
                <p className="text-sm text-slate-600 mt-2 leading-relaxed">{t.tagline}</p>
                <span className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-[#0033A0]">
                  Learn more <ArrowRight size={14} weight="bold" className="transition-transform group-hover:translate-x-0.5" />
                </span>
              </Link>
            );
          })}
        </div>
      </section>

      {/* MIDDLE FEATURE — Families */}
      <section className="bg-slate-50 py-20 md:py-28 border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-6 md:px-10 grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16 items-center">
          <div className="md:col-span-6 order-2 md:order-1">
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-[#0033A0]">For families</p>
            <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tight text-slate-900 mt-3">
              Benefits don{`'`}t just cover you — they cover the people you love.
            </h2>
            <p className="text-slate-700 mt-5 leading-relaxed">
              Spouses and kids get TRICARE healthcare, base housing, commissary access, scholarships, and (in many cases) transferable GI Bill benefits. We explain what{`'`}s available, what{`'`}s automatic, and what you have to apply for.
            </p>
            <div className="mt-7 flex flex-wrap gap-3">
              <Link
                to="/topics/healthcare"
                data-testid="families-tricare-btn"
                className="inline-flex items-center gap-2 bg-[#0033A0] hover:bg-[#002277] text-white font-semibold py-3 px-6 rounded-md transition-colors"
              >
                TRICARE basics <ArrowRight size={16} weight="bold" />
              </Link>
              <Link
                to="/topics/education"
                data-testid="families-education-btn"
                className="inline-flex items-center gap-2 bg-white hover:bg-slate-100 text-slate-900 border border-slate-300 font-semibold py-3 px-6 rounded-md transition-colors"
              >
                Transfer the GI Bill
              </Link>
            </div>
          </div>
          <div className="md:col-span-6 order-1 md:order-2">
            <div className="relative rounded-2xl overflow-hidden shadow-xl">
              <img
                src="https://images.pexels.com/photos/7985795/pexels-photo-7985795.jpeg"
                alt="A service member reuniting with their family"
                className="w-full h-[360px] md:h-[460px] object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent" />
            </div>
          </div>
        </div>
      </section>

      {/* BRANCH STRIP */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 py-20 md:py-28">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <p className="text-xs font-bold uppercase tracking-[0.25em] text-[#0033A0]">Six branches</p>
          <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tight text-slate-900 mt-2">
            Which path fits you?
          </h2>
          <p className="text-slate-600 mt-3 leading-relaxed">
            Different missions, different jobs, very different lifestyles. Tap a branch to see what they do and what jobs are available.
          </p>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
          {BRANCHES.map((b) => (
            <Link
              key={b.slug}
              to={`/branches/${b.slug}`}
              data-testid={`branch-tile-${b.slug}`}
              className="relative overflow-hidden rounded-xl border border-slate-200 hover:border-slate-900 hover:shadow-md transition-all p-5 group bg-white"
            >
              <div className="h-2 absolute top-0 left-0 right-0" style={{ background: b.color }} />
              <h3 className="font-display font-bold text-slate-900 text-base mt-2">{b.name}</h3>
              <p className="text-xs text-slate-500 mt-1 line-clamp-2 leading-snug">{b.mission}</p>
              <span className="absolute bottom-4 right-4 text-slate-400 group-hover:text-[#0033A0] transition-colors">
                <ArrowRight size={16} weight="bold" />
              </span>
            </Link>
          ))}
        </div>
      </section>

      {/* PREMIUM FEATURE */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 pb-20 md:pb-28">
        <div className="relative overflow-hidden rounded-2xl border border-slate-200 bg-white p-8 md:p-12 grid grid-cols-1 md:grid-cols-12 gap-8 items-center">
          <div className="md:col-span-7">
            <span className="inline-flex items-center gap-2 bg-[#0033A0]/10 text-[#0033A0] px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider">
              Premium · $1 · <span className="text-[#D90429]">90% off</span>
            </span>
            <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tight text-slate-900 mt-4 leading-tight">
              Career Deep Dive — your top 3 jobs, fully explained.
            </h2>
            <p className="text-slate-700 mt-4 leading-relaxed max-w-xl">
              Pick a branch and the jobs you{`'`}re curious about. Try job #1 free, then unlock the full deep dive for <strong>$1</strong> <span className="text-slate-400 line-through">$10</span>. Tailored report covering daily duties, training pipeline, civilian certifications, and after-service career paths. <strong>50% of net profit</strong> goes to veteran-support organizations.
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <Link to="/career-deepdive" data-testid="home-deepdive-cta" className="inline-flex items-center gap-2 bg-[#0033A0] hover:bg-[#002277] text-white font-semibold py-3 px-6 rounded-md transition-colors">
                Try free preview <ArrowRight size={16} weight="bold" />
              </Link>
              <span className="text-xs text-slate-500 self-center">One-time purchase · Buy once, view forever · 50% launch discount</span>
            </div>
          </div>
          <div className="md:col-span-5">
            <div className="rounded-xl overflow-hidden border border-slate-200">
              <img
                src="https://images.pexels.com/photos/7984336/pexels-photo-7984336.jpeg"
                alt=""
                className="w-full h-[260px] md:h-[320px] object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="bg-[#0A1128] text-white">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-20 md:py-28 grid grid-cols-1 md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-7">
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-[#FCA5A5]">Still figuring it out?</p>
            <h2 className="font-display text-3xl md:text-5xl font-bold tracking-tight mt-3 leading-tight">
              Start with the basics. <br className="hidden md:block" />Six steps, zero jargon.
            </h2>
            <p className="text-slate-300 mt-5 max-w-xl leading-relaxed">
              The Start Here guide walks you from {`"I'm thinking about it"`} to {`"I'm ready to talk to a recruiter"`} — without pressure or sales talk.
            </p>
          </div>
          <div className="md:col-span-5 md:text-right">
            <Link
              to="/start-here"
              data-testid="footer-cta-start-here"
              className="inline-flex items-center gap-2 bg-[#D90429] hover:bg-[#B00020] text-white font-semibold py-4 px-8 rounded-md text-lg transition-colors"
            >
              Open Start Here Guide <ArrowRight size={18} weight="bold" />
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
}
